import { entityTypeSchema } from '@/lib/route-search';

import type { Annotation } from './message-votes';
import type { z } from 'zod';

export interface EntityVote {
  id: number;
  user_email: string;
  user_name: string | null;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  direction: number; // 1 for upvote, -1 for downvote
  reasoning: string | null;
  status: string | null;
  tags: string[];
  has_notes: boolean;
  created_at: string;
  updated_at: string;
}

export interface MeetingDetails {
  meeting_id: string;
  name: string | null;
  summary: string | null;
  tldr: string | null;
  notes: string | null;
  personified_transcript: string | null;
  status: string;
  created_at: string;
}

export interface MeetingPrepDetails {
  prep_id: string;
  meeting_id: string;
  meeting_name: string | null;
  agent_name: string;
  content: string;
  created_at: string;
  chat_id: string | null;
  chat_history: string[][] | null;
}

export interface MeetingAssistAutoReview {
  needs_approval: boolean;
  risk_level: string | null;
  review_failed: boolean;
}

// An action card's payload, whose fields depend on its `type`
export type ActionCardContent = { type: string } & Record<string, unknown>;

// What a Meeting Assist card held when the user voted
export interface MeetingAssistCardAsSeen {
  title: string;
  status: string;
  content: ActionCardContent;
  // The agent's latest version, when `content` differs from it because the
  // user edited the card or running it changed it. Null when they match
  original_content: ActionCardContent | null;
  agent_action: string | null;
  result: unknown;
  external_ref: unknown;
  dismiss_feedback: string | null;
  revised_at: string | null;
  resolved_at: string | null;
  dismissed_at: string | null;
}

export interface MeetingAssistCard {
  local_id: number;
  action_type: string;
  context: string | null;
  importance: number | null;
  auto_review: MeetingAssistAutoReview | null;
  assistant_message_id: string | null;
  created_at: string;
  // Null when the card changed after the vote, since no card keeps its earlier
  // versions. The fields above are set when the card is made
  as_seen: MeetingAssistCardAsSeen | null;
}

export interface MeetingAssistDetails {
  meeting_id: string;
  meeting_name: string | null;
  // Null when no suggestions turn ran for the meeting
  chat_id: string | null;
  chat_history: string[][];
  cards: MeetingAssistCard[];
}

export interface RoutineReportDetails {
  report_id: string;
  title: string | null;
  text: string | null;
  date: string;
  routine_id: number;
  template_title: string | null;
  experiment_slug: string | null;
  rollout_slug: string | null;
  prompt: string | null;
  prompt_version: string | null;
  created_at: string;
}

export interface ActionCardChat {
  chat_id: string;
  // The turns that ran after the vote are redacted
  chat_history: string[][];
}

export interface ActionCardWatch {
  routine_id?: number | null;
  run_chat_id?: string | null;
  last_checked_at: string | null;
  suggestion: 'complete' | 'dismiss' | null;
  reason: string | null;
  note: string | null;
  resolved: 'complete' | 'dismiss' | null;
  kept?: boolean;
}

export interface ActionCardChild {
  local_id: number;
  action_type: string;
  status: string;
  title: string | null;
}

export interface ActionCardDetails {
  card_id: string;
  local_id: number;
  origin: string;
  action_type: string;
  context: string | null;
  importance: number | null;
  auto_review: MeetingAssistAutoReview | null;
  created_at: string;
  expires_at: string | null;
  pinned_at: string | null;
  snoozed_until: string | null;
  // The card as it stands now; no card keeps its earlier versions
  state: MeetingAssistCardAsSeen;
  changed_after_vote: boolean;
  parent_local_id: number | null;
  children: ActionCardChild[];
  routine_report_id: number | null;
  // The chat whose tool call made the card: a routine run's hidden chat, or the user's chat
  source_chat: ActionCardChat | null;
  assistant_message_id: string | null;
  follow_up_chat: ActionCardChat | null;
  // Null when no watcher ever wrote to the card
  watcher: {
    watch: ActionCardWatch;
    // The run that last recorded a finding on the card, by the vote
    run: ActionCardChat | null;
  } | null;
  // The latest watcher runs between the card's making and the vote, newest first, other than `watcher.run`: the runs
  // that looked at it, whether or not they recorded a finding on it
  watcher_runs_before_vote: { ran_at: string; chat: ActionCardChat }[];
}

export interface EntityVoteDetails {
  vote_id: number;
  entity_type: string;
  entity_id: string;
  direction: number;
  reasoning: string | null;
  annotation: Annotation | null;
  meeting: MeetingDetails | null;
  meeting_prep: MeetingPrepDetails | null;
  meeting_assist: MeetingAssistDetails | null;
  // For a vote on an action card, the routine run that made the card
  routine_report: RoutineReportDetails | null;
  action_card: ActionCardDetails | null;
}

export type EntityType = z.infer<typeof entityTypeSchema>;

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  meeting: 'Meeting Notes',
  meeting_prep: 'Meeting Prep',
  meeting_assist: 'Meeting Assist',
  routine_report: 'Routine',
  im_channel_proactive_routine: 'IM Channel Proactive Routine',
  action_card: 'Action Card',
};

export const ENTITY_TYPE_OPTIONS = entityTypeSchema.options.map((value) => ({
  value,
  label: ENTITY_TYPE_LABELS[value],
}));

export function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType as EntityType] ?? entityType;
}
